/**
 * tailwindClassnames.tsx
 *
 * This file contains reusable Tailwind CSS class combinations for consistent styling across the application.
 */

// Layout and Container Classes
export const containerClasses = {
  page: 'bg-unlisted-very-light-gray transition-all duration-300',
  // The pull-up has to equal the gap NewApp leaves under the fixed bar —
  // pt-[94px] over a 70px desktop bar, pt-[104px] over the 80px mobile one,
  // so 24px both times. At -mt-5 it came up 4px short and that sliver of the
  // parent's teal tint showed as a stray band under the bar's border. pt-6
  // grows by the same 4px, so the content above does not move.
  content: 'w-full max-w-content mx-auto',
  card: 'bg-white shadow rounded-md p-4 transition-all duration-300',
  header:
    'bg-white shadow static inset-x-0 px-4 md:px-8 desktop:px-[60px] pt-6 -mt-6 -mx-4 md:-mx-8 desktop:-mx-[60px] transition-all duration-300',
};

// The main list sections — Routes, Connections, Sources, Workspaces. Their
// title sits on a white band with the rest of the page on a teal tint below it.
// Both bleed out of the app shell's gutters (-mx) and re-add them (px) so the
// colours run edge to edge while their contents stay on the content column.
export const listPageClasses = {
  // -mt-6 cancels the shell's 24px of breathing room so the band sits flush
  // against the fixed top bar. At least 115px per the design; the row inside wraps
  // on a phone, when the title and the action no longer fit side by side.
  band: 'bg-white min-h-[115px] py-4 flex items-center px-4 md:px-8 desktop:px-[60px] -mx-4 md:-mx-8 desktop:-mx-[60px] -mt-6',
  // min-height keeps the tint filling the viewport when a list is short:
  // 100vh less the top bar (80px on a phone, 70px from md) and the 115px band. -mb-6 swallows the app
  // shell's bottom padding, which otherwise shows as a white strip along the
  // bottom edge; pb-10 puts that breathing room back inside the tint.
  body: 'bg-teal-t10 px-4 md:px-8 desktop:px-[60px] -mx-4 md:-mx-8 desktop:-mx-[60px] -mb-6 pt-8 pb-10 min-h-[calc(100vh-195px)] md:min-h-[calc(100vh-185px)]',
  // The page title. textClasses.heading's mb-3 would push it off-centre in a
  // band.
  title: 'text-3xl md:text-4xl font-medium text-ui-text-primary-text',
  // The band's inner row: title left, primary action right, on the same
  // content column as everything below.
  bandRow:
    'w-full max-w-list-table mx-auto flex flex-wrap justify-between items-center gap-3',
};

// The creation flows — New Route steps 1 and 2, and New Connection. The same
// white title band and teal tint as the sections you browse, so creating
// something looks like the rest of the product. `body` differs from the list
// one only in bottom padding, which has to clear the fixed action footer.
export const wizardClasses = {
  band: listPageClasses.band,
  body: 'bg-teal-t10 px-4 md:px-8 desktop:px-[60px] -mx-4 md:-mx-8 desktop:-mx-[60px] -mb-6 pt-8 pb-24 min-h-[calc(100vh-195px)] md:min-h-[calc(100vh-185px)]',
  // Solid tint rather than translucent white over a blur, which smears grey
  // over a coloured page. No rule along the top: the buttons sit on the same
  // tint, so a line just draws a seam across one surface.
  footer: 'fixed bottom-0 left-0 w-full z-10 py-3 bg-teal-t10 md:pl-[110px]',
  // Keeps the band's title, the form and the footer buttons on one column.
  row: 'w-full max-w-list-table mx-auto',
  title: listPageClasses.title,
};

// The help side panel's article cards: light teal like the pages behind them,
// with the primary button treatment on the link. `buttonClasses.primary` guards
// its hover with `enabled:`, which never matches an <a>, hence the hover here.
export const helpCardClasses = {
  card: 'flex flex-col items-start rounded p-4 gap-[10px] bg-teal-t10',
  button:
    'inline-flex items-center justify-center no-underline mt-1 hover:bg-green-1',
};

// Text Classes
export const textClasses = {
  heading: 'text-4xl font-medium mb-3 text-ui-text-primary-text',
  subheading: 'text-xl font-semibold text-ui-text-primary-text',
  body: 'text-ui-text-primary-text',
  label: 'text-sm font-medium text-ui-text-primary-text',
};

// Button Classes
export const buttonClasses = {
  // The two full-size buttons — primary and Cancel — share one shape
  // and one type: 3px radius, 8px/28px padding, Strata/Body/LG (18px, weight
  // 400, 28.8px line-height). They differ only in fill. The design names
  // Roboto as the family; the app's own stack is kept rather than loading a
  // second body font for two controls.
  //
  // Disabled is the same everywhere: Greyscale/10 fill under UI/Text/Disabled.
  // Hover has no token in the design at all, so each one steps once along its
  // own ramp (see the token reference page for what that means per button).
  //
  // Single source of truth — call sites toggle the `disabled` prop instead of
  // swapping classes, and add only layout extras (margins, sizing).
  primary:
    'border border-transparent bg-brand-accent-green enabled:hover:bg-green-1 text-white text-[18px] font-normal leading-[28.8px] rounded-[3px] px-7 py-2 disabled:bg-greyscale-10 disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-300',
  link: 'text-brand-accent-green hover:text-green-1 underline transition-colors duration-300',
  // Green inline text link (flow-map "View Connection", drawer "Create new
  // destination", shared-account Route links). Layout extras only at call sites.
  inlineLink: 'text-green-1 text-sm font-semibold underline underline-offset-2',
  // The three buttons at control scale, for the builder toolbars and panels
  // where they sit beside a segmented toggle or a select — at full size they
  // towered over them. Body/SM type at normal weight (like every other button
  // size) and the segmented control's own padding,
  // so everything on the row lines up. Colours and states are the full-size
  // ones, unchanged.
  primarySm:
    'border border-transparent bg-brand-accent-green enabled:hover:bg-green-1 text-white text-sm font-normal leading-[21px] rounded-[3px] px-3 py-1.5 disabled:bg-greyscale-10 disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-300',
  cancelSm:
    'bg-white border border-ui-text-secondary-text text-ui-text-secondary-text enabled:hover:bg-teal-t10 disabled:bg-greyscale-10 disabled:border-transparent disabled:text-ui-text-disabled disabled:cursor-not-allowed text-sm font-normal leading-[21px] rounded-[3px] px-3 py-1.5 transition-colors duration-300',
  // A step smaller than cancelSm, for an action inside a list row: it sits beside
  // the row's own text and should not outweigh it.
  cancelXs:
    'bg-white border border-ui-text-secondary-text text-ui-text-secondary-text enabled:hover:bg-teal-t10 text-xs font-normal leading-[18px] rounded-[3px] px-2.5 py-[5px] transition-colors duration-300',
  // The destructive button at the same scale as primarySm / cancelSm, for a
  // footer where it sits beside them and must line up.
  destructiveSm:
    'border border-transparent bg-ui-status-error-destructive enabled:hover:bg-red-2 text-white text-sm font-normal leading-[21px] rounded-[3px] px-3 py-1.5 disabled:bg-greyscale-10 disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-300',
  lightGreenSm:
    'border border-transparent bg-ui-cta-secondary-blue text-ui-text-primary-text enabled:hover:bg-teal-t30 text-sm font-normal leading-[21px] rounded-[3px] px-3 py-1.5 disabled:bg-greyscale-10 disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-300',
  // Outlined, 30px tall: a light gray border, no fill, black text. The
  // webhook tab's secondary buttons all use it (Generate from Sample,
  // Add / Add Field, Save / Cancel, the Builder view switch).
  outlineXs:
    'bg-white border border-gray-lines text-ui-text-primary-text enabled:hover:bg-greyscale-11 text-sm font-normal leading-5 rounded-[3px] px-3 py-1 disabled:bg-greyscale-10 disabled:border-transparent disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-300',
  // The light-blue button at a 15.3px scale for the Add Item button of
  // config-form lists, which sits beside a field title and should not
  // outweigh it.
  listAdd:
    'border border-transparent bg-ui-cta-secondary-blue text-ui-text-primary-text enabled:hover:bg-teal-t30 text-[15.3px] font-normal leading-[24.5px] rounded-[3px] py-[6.8px] px-[23.8px] disabled:bg-greyscale-10 disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-300',
  // Two button tiers. The full-size primary and cancel are
  // for a page's main action only (Create Route, Create Workspace, Create
  // Connection, Add User). Every other action uses this smaller tier: 16px
  // type, 8px/16px padding, same fills and states. The light-blue member is
  // headerActionGreen below.
  primaryAction:
    'border border-transparent bg-brand-accent-green enabled:hover:bg-green-1 text-white text-base font-normal leading-6 rounded-[3px] px-4 py-2 disabled:bg-greyscale-10 disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-300',
  destructiveAction:
    'border border-transparent bg-ui-status-error-destructive enabled:hover:bg-red-2 text-white text-base font-normal leading-6 rounded-[3px] px-4 py-2 disabled:bg-greyscale-10 disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-300',
  cancelAction:
    'bg-white border border-ui-text-secondary-text text-ui-text-secondary-text enabled:hover:bg-teal-t10 disabled:bg-greyscale-10 disabled:border-transparent disabled:text-ui-text-disabled disabled:cursor-not-allowed text-base font-normal leading-6 rounded-[3px] px-4 py-2 transition-colors duration-300',
  // The pair that sits in the flow-map headers (Add destination, Remove from
  // Route). Remove is a step smaller so it does not outweigh the header.
  // headerActionGreen is also the light-blue member of the smaller tier.
  headerActionGreen:
    'border border-transparent bg-ui-cta-secondary-blue text-ui-text-primary-text enabled:hover:bg-teal-t30 text-base font-normal leading-6 rounded-[3px] px-4 py-2 disabled:bg-greyscale-10 disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-300',
  headerActionRed:
    'border border-transparent bg-ui-status-error-destructive text-white enabled:hover:bg-red-2 text-sm font-normal leading-5 rounded-[3px] px-3 py-1.5 disabled:bg-greyscale-10 disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-200',
  // Canonical "Clear All Filters" button, per design: white on a 1px
  // Brand Text Primary border, 3px radius, 38px tall, 8px/12px padding.
  // Call sites add only layout extras (margins, whitespace-nowrap).
  // Sits on the page tint, so its hover goes one step darker than that tint
  // rather than matching it.
  clearFilters:
    'flex items-center justify-center gap-2 h-[38px] px-3 py-2 bg-white border border-ui-text-primary-text text-ui-text-primary-text rounded-[3px] hover:bg-teal-t20 transition-colors duration-300',
  // The same delete treatment at row scale (Remove member, Leave workspace,
  // Remove from Route). Fill and hover match `destructiveAction`; only the size
  // differs, so a removal reads the same wherever it is offered. Single
  // source of truth — call sites toggle the `disabled` prop and add only
  // layout extras (margins).
  lightRed:
    'bg-ui-status-error-destructive text-white rounded-[3px] px-3 py-1 text-sm enabled:hover:bg-red-2 disabled:bg-greyscale-10 disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-200',
  // Canonical Cancel button: white, outlined and labelled in Secondary Text,
  // so it reads as an action beside the filled two without competing with
  // them. Single source of truth for every Cancel across the app; call sites
  // add only layout extras (margins).
  cancel:
    'bg-white border border-ui-text-secondary-text text-ui-text-secondary-text enabled:hover:bg-teal-t10 disabled:bg-greyscale-10 disabled:border-transparent disabled:text-ui-text-disabled disabled:cursor-not-allowed text-[18px] font-normal leading-[28.8px] rounded-[3px] px-7 py-2 transition-colors duration-300',
  // The destructive/cancel pair at Strata/Body/MD (16px, 25.6px line-height) for
  // confirmation dialogs, where the full-size pair towers over a short body text.
  // Colours and states match `destructiveAction` / `cancel`; only the type scale differs,
  // so both buttons in a dialog always share one size.
  destructiveMd:
    'border border-transparent bg-ui-status-error-destructive enabled:hover:bg-red-2 text-white text-[16px] font-normal leading-[25.6px] rounded-[3px] px-5 py-1.5 disabled:bg-greyscale-10 disabled:text-ui-text-disabled disabled:cursor-not-allowed transition-colors duration-300',
  cancelMd:
    'bg-white border border-ui-text-secondary-text text-ui-text-secondary-text enabled:hover:bg-teal-t10 disabled:bg-greyscale-10 disabled:border-transparent disabled:text-ui-text-disabled disabled:cursor-not-allowed text-[16px] font-normal leading-[25.6px] rounded-[3px] px-5 py-1.5 transition-colors duration-300',
};

// Segmented toggle (e.g. Code/Builder, View/Edit). Single source of truth for the
// toggle look — change the selected color here and every toggle updates. Consumed
// via the <SegmentedToggle> component.
export const toggleClasses = {
  container: 'inline-flex rounded bg-unlisted-light-gray p-0.5',
  option:
    'px-3 py-1.5 text-sm font-medium rounded-sm cursor-pointer transition-colors duration-200',
  active: 'bg-ui-cta-primary text-white',
  inactive:
    'bg-transparent text-ui-text-secondary-text hover:text-ui-text-primary-text',
};

// Table Classes
export const tableClasses = {
  // Teal T30 throughout, outer border and row separators alike.
  container:
    'table-auto h-auto w-full border border-teal-t30 text-ui-text-primary-text rounded-md bg-white transition-all duration-300',
  fixedContainer:
    'table-fixed h-auto w-full border border-teal-t30 text-ui-text-primary-text rounded-md bg-white transition-all duration-300',
  // Brand teal with reversed text: the strongest horizontal rule on a list page.
  header:
    'px-3 pt-3 pb-2 font-semibold text-base bg-teal-t100 text-white text-left transition-all duration-300',
  headerCell: 'p-3',
  row: 'bg-white border-b border-teal-t30 last:border-b-0 hover:bg-greyscale-11 align-top transition-all duration-300',
  cell: 'p-3',
  sortIcon: 'cursor-pointer',

  // Status styles
  statusHealthy: 'flex items-center text-ui-cta-primary',
  statusWarning: 'flex items-center text-status-orange',
  statusError: 'flex items-center text-ui-status-error-destructive',
};

// Filter and Search Classes
// Text sitting directly on the teal page tint (not inside a white card or
// table): Brand Text Secondary at Strata/Body/SM, per design.
// Text sitting directly on the page tint: Secondary Text, never the primary
// near-black, which is reserved for text on white.
export const onTealText =
  'text-[14px] font-normal leading-[21px] text-ui-text-secondary-text';

export const filterClasses = {
  container:
    'text-ui-text-primary-text bg-unlisted-very-light-gray mr-3 mb-3 h-auto px-3 pb-3 divide-y divide-unlisted-gray-lines overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-unlisted-neutral-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-unlisted-neutral-gray-500 transition-all duration-300 w-80 flex-shrink-0',
  section: 'my-6 pt-3',
  // Same tokens as SELECTED_FILTER_CHIP in selectinputstyles: Teal T20 fill,
  // Brand Text Primary text, Strata/Body/SM. Kept in step by hand because one
  // is emotion (react-select) and the other Tailwind.
  tag: 'text-sm font-normal leading-[21px] w-fit py-1 px-2 bg-teal-t20 text-ui-text-primary-text mt-1 rounded-[3px] items-end',
  tagCount: 'bg-ui-cta-primary text-white text-sm py-1 px-3 rounded-full',
  header: 'flex flex-row justify-between mt-2 pt-2',
  // The filter rows sit on the page tint, so their labels take the on-tint
  // treatment. A filter label inside a white panel needs its own class.
  label: onTealText,
};

// Pagination Classes
export const paginationClasses = {
  // Transparent: this row used to carry its own near-white fill, which read as
  // a white footer strip once the list sections gained their teal tint. It now
  // takes whatever is behind it, on tinted and white pages alike.
  container: 'w-full mt-4 flex flex-row items-center justify-between text-base',
  // The page and prev/next controls: white, lightly bordered, Brand Text
  // Primary — the same chrome the rest of the new design uses.
  // The pagination row sits on the page's teal-t10 tint, so hovering to that
  // same tint made the button dissolve into the background. One step darker.
  button:
    'flex items-center justify-center h-9 min-w-[36px] px-2 bg-white border border-unlisted-gray-lines rounded-md text-ui-text-primary-text hover:bg-teal-t20 transition-colors duration-300',
  buttonActive: 'bg-teal-t20 border-teal-t20 font-medium',
  text: onTealText,
};

// Form Classes
export const formClasses = {
  group: 'mb-4',
  label: 'block text-sm font-medium text-ui-text-primary-text mb-1',
  input:
    'w-full p-2 border border-unlisted-gray-lines rounded focus:outline-none focus:ring-2 focus:ring-ui-cta-primary',
  select:
    'w-full p-2 border border-unlisted-gray-lines rounded focus:outline-none focus:ring-2 focus:ring-ui-cta-primary',
  checkbox: 'mr-2 rounded text-ui-cta-primary focus:ring-ui-cta-primary',
  error: 'mt-1 text-sm text-ui-status-error-destructive',
};

// Modal Classes
export const modalClasses = {
  overlay:
    'fixed inset-0 bg-greyscale-3 opacity-80 flex justify-center items-center z-50 transition-colors duration-300',
  container: 'bg-white rounded-md p-6 max-w-lg w-full shadow-xl',
  header: 'text-xl font-bold mb-4 text-ui-text-primary-text',
  body: 'mb-6 text-ui-text-primary-text',
  footer: 'flex justify-end space-x-2',
};

// Status and Notification Classes
export const statusClasses = {
  success: 'text-ui-cta-primary',
  warning: 'text-status-orange',
  error: 'text-ui-status-error-destructive',
  info: 'text-unlisted-blue-basic',
};

// Radio Select List Classes
export const radioSelectClasses = {
  container: 'flex flex-col max-w-xs',
  header: 'w-full flex flex-row justify-between',
  sectionLabel: 'font-semibold text-ui-text-primary-text',
  input:
    'my-2 p-1.5 border border-unlisted-gray-lines rounded rounded-sm px-2 pr-8 w-full transition-all duration-300',
  clearButton:
    'absolute right-1.5 top-1/2 transform -translate-y-1/2 text-ui-cta-primary',
  listContainer:
    'max-h-40 overflow-auto scrollbar-thin scrollbar-thumb-unlisted-neutral-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-unlisted-neutral-gray-500',
  itemContainer: 'flex flex-row',
  radioInput: 'accent-ui-cta-primary cursor-pointer',
  radioLabel: 'inline-block mx-2 text-ui-text-primary-text truncate',
  radioLabelHover:
    'inline-block hover:cursor-pointer mx-2 text-ui-text-primary-text truncate',
  loadMoreLabel: 'text-ui-cta-primary text-sm cursor-pointer',
  loadingLabel: 'text-ui-text-primary-text text-sm cursor-pointer',
};

// Select List Classes (for checkboxes)
export const selectListClasses = {
  container: 'flex flex-col max-w-xs',
  header: 'w-full flex flex-row justify-between',
  sectionLabel: 'font-semibold text-ui-text-primary-text',
  input:
    'my-2 p-1.5 border border-unlisted-gray-lines rounded rounded-sm px-2 pr-8 w-full transition-all duration-300',
  clearButton:
    'absolute right-1.5 top-1/2 transform -translate-y-1/2 text-ui-cta-primary',
  listContainer:
    'max-h-40 overflow-auto scrollbar-thin scrollbar-thumb-unlisted-neutral-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-unlisted-neutral-gray-500',
  itemContainer: 'flex flex-row',
  checkboxInput: 'accent-ui-cta-primary cursor-pointer',
  checkboxLabel: 'inline-block mx-2 text-ui-text-primary-text truncate',
  checkboxLabelHover:
    'inline-block hover:cursor-pointer mx-2 text-ui-text-primary-text truncate',
  loadMoreLabel: 'text-ui-cta-primary text-sm cursor-pointer',
  loadingLabel: 'text-ui-text-primary-text text-sm cursor-pointer',
};

// Route Status Classes
// Shared pill layout used by Route status badges AND the detail-page header info pills.
// flex, 24px tall, 6px/3px padding, 4px gap, 3px radius, 15px/normal. Colors (bg/text) supplied per use.
const statusPillBase =
  'flex items-center gap-1 h-6 px-[6px] py-[3px] rounded-[3px] text-[15px] font-normal leading-normal transition-all duration-300';
// List badge: compact, centered.
const statusListBase =
  'text-xs text-center rounded rounded-sm transition-all duration-300';
// Compose a status style: same shape everywhere, only the colors change.
const statusStyle = (colors: string) => (detailsPage: boolean) =>
  detailsPage ? `${statusPillBase} ${colors}` : `${statusListBase} ${colors}`;

export const routeStatusClasses = {
  // Specific status styles — shape is shared, only colors differ
  healthy: statusStyle('bg-teal-t10 text-ui-cta-primary'),
  unhealthy: statusStyle(
    'bg-ui-status-bg-error-bg text-ui-status-error-destructive',
  ),
  needsReview: statusStyle('bg-unlisted-warning-yellow text-yellow-1'),
  disabled: statusStyle('bg-unlisted-light-gray text-ui-text-primary-text'),
  unknown: statusStyle('bg-unlisted-light-gray text-ui-text-primary-text'),
};

// Route Details Classes
export const routeDetailsClasses = {
  container: 'relative h-full',
  header:
    'bg-white shadow static inset-x-0 px-4 md:px-8 desktop:px-[60px] pt-6 -mt-6 -mx-4 md:-mx-8 desktop:-mx-[60px] transition-all duration-300',
  // The Route details tab strip: pinned so the tabs survive any amount of
  // scrolling. It parks directly under the fixed top bar — 70px on desktop,
  // the 80px mobile bar below md. The bar itself stays put: it carries the
  // timezone picker, notifications and profile menu, and losing those on
  // scroll would be a regression.
  // Bleeds out of the page gutters like `header` does, and stays under the
  // drawers (z-56+).
  stickyTabs:
    'sticky top-[80px] md:top-[70px] z-40 bg-white px-4 md:px-8 desktop:px-[60px] -mx-4 md:-mx-8 desktop:-mx-[60px]',
  title: 'text-3xl font-semibold text-ui-text-primary-text',
  infoLabel: 'text-ui-text-primary-text mt-2 mb-3 mr-1 text-sm',
  infoPill:
    'text-ui-text-primary-text mt-2 mb-3 bg-unlisted-light-gray rounded rounded-sm px-2 py-1 mr-3 text-sm',
  // Header info pill: same pill shape as the status badges, light teal fill,
  // no border.
  headerPill: `${statusPillBase} bg-teal-t10 text-ui-text-secondary-text`,
  separator: 'text-ui-text-primary-text mt-2 mb-3 mr-2',
  enabledPanel:
    'flex flex-row items-center px-2 py-3 gap-4 w-fit border border-unlisted-enabled-panel-border rounded transition-all duration-300',
  disabledPanel:
    'flex flex-row items-center px-2 py-3 gap-4 w-fit border border-unlisted-gray-lines rounded transition-all duration-300',
  tabActive:
    'border-2 border-white border-b-ui-cta-primary text-ui-text-primary-text mr-3 py-3 cursor-pointer transition-all duration-300',
  tabInactive:
    'border-2 border-white mr-3 py-3 cursor-pointer text-ui-text-primary-text transition-all duration-300',
  switchText:
    'font-semibold text-ui-text-primary-text flex flex-row items-center',
  switchTextLabel: 'mr-2 whitespace-nowrap',
  switchContainer:
    'group relative flex h-7 w-14 shrink-0 cursor-pointer rounded-full p-1 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 data-[focus]:outline-1 data-[focus]:outline-white',
};

// Skeleton Loading Classes
export const skeletonClasses = {
  // Teal T30 on every surface — white cards, tables and the page tint alike —
  // so there is one skeleton colour rather than one per background.
  base: 'bg-teal-t30 rounded animate-pulse',
};

// Text Input Classes
export const inputClasses = {
  standard:
    'p-1.5 h-auto border border-unlisted-neutral-gray-200 rounded text-ui-text-primary-text transition-all duration-300',
  disabled:
    'p-1.5 h-auto border border-unlisted-neutral-gray-200 rounded bg-unlisted-light-gray text-ui-text-primary-text transition-all duration-300',
  error:
    'p-1.5 h-auto border border-unlisted-orange rounded text-ui-text-primary-text transition-all duration-300',
};

// Global Search Classes
export const globalSearchClasses = {
  container: 'flex flex-col max-w-xs items-start transition-all duration-300',
  inputContainer: 'flex flex-row',
  // self-start, so the wrapper is the input's height rather than stretching to
  // the row's: the clear "x" is positioned inside it, and on a stretched
  // wrapper "centred" put it ~4.5px below the input's own centre.
  inputWrapper: 'relative self-start',
  // Border, text colour and type all matching the filter dropdowns beside it:
  // the box had no colour of its own, so its text came out darker than a
  // dropdown's selected value.
  input:
    'h-[38px] border border-ui-text-secondary-text rounded-[3px] px-2 pr-8 text-ui-text-primary-text text-[14px] font-normal leading-[21px] transition-all duration-300',
  // The applied state, shared with the pages that roll their own search input
  // so it cannot drift from this one again.
  applied: 'bg-teal-t20 shadow-[inset_0_0_0_3px_white]',
  // Centred by the box model — stretched to the input's full height with auto
  // margins, then the glyph centred inside a square of its own. A transform or
  // a top margin both depend on the icon's intrinsic box, which is what left
  // it off-centre before. right-2 clears the 3px inset ring the applied state
  // draws.
  clearButton:
    'absolute right-2 top-0 bottom-0 my-auto flex h-5 w-5 items-center justify-center leading-none text-ui-text-secondary-text transition-all duration-300',
  searchButton:
    'mx-2 mb-2 bg-brand-accent-green hover:bg-ui-cta-primary text-white rounded-[3px] text-sm p-2 h-auto transition-all duration-300',
}; /**
 * Helper function to combine class names
 * @param classes - Array of class names
 * @returns Combined class names string
 */
export const combineClasses = (
  ...classes: (string | undefined | null | false)[]
): string => {
  return classes.filter(Boolean).join(' ');
};

// Example usage of combineClasses:
// const tableHeaderClass = combineClasses(tableClasses.headerCell, sortable && tableClasses.sortIcon);
